import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@shared/ui';
import type { AutoWithdrawalRoutingMode } from '@shared/api/contracts';
import styles from './auto-withdraw-ui.module.css';

interface ModeSelectorProps {
  value: AutoWithdrawalRoutingMode;
  disabled?: boolean;
  onChange: (mode: AutoWithdrawalRoutingMode) => void;
}

const MODES: AutoWithdrawalRoutingMode[] = ['SINGLE_TARGET_SWAP', 'DIRECT_BY_NETWORK'];

export const ModeSelector = ({ value, disabled = false, onChange }: ModeSelectorProps) => {
  const { t } = useTranslation('cabinet');

  return (
    <Tabs
      value={value}
      onValueChange={(next) => onChange(next as AutoWithdrawalRoutingMode)}
      aria-label={t('withdrawal.auto.modeSelector.label')}
    >
      <TabsList
        className={cn(
          'grid h-auto w-full grid-cols-1 items-stretch gap-3 overflow-visible bg-transparent p-0 sm:grid-cols-2',
          disabled && 'pointer-events-none opacity-60',
        )}
      >
        {MODES.map((mode) => (
          <TabsTrigger
            key={mode}
            value={mode}
            disabled={disabled}
            className={cn(
              'relative flex h-full min-h-[6.5rem] w-full flex-col items-start justify-start gap-2 rounded-xl border p-4 pl-10 text-left shadow-none',
              'overflow-visible whitespace-normal data-[state=active]:border-primary data-[state=active]:bg-primary/5 data-[state=active]:shadow-[inset_0_0_0_1px_hsl(var(--primary))]',
              'data-[state=inactive]:border-border data-[state=inactive]:bg-card',
            )}
          >
            <span
              aria-hidden
              className={cn(
                'absolute left-4 top-[1.125rem] flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground/50',
                value === mode && 'border-primary',
              )}
            >
              <span
                className={cn(
                  'h-2 w-2 rounded-full bg-primary transition-transform',
                  value === mode ? 'scale-100' : 'scale-0',
                )}
              />
            </span>
            <span className="text-sm font-semibold leading-snug">{t(`withdrawal.auto.modes.${mode}.title`)}</span>
            <span className="text-xs font-normal leading-snug text-muted-foreground">
              {t(`withdrawal.auto.modes.${mode}.description`)}
            </span>
            <span className="mt-0.5 flex flex-wrap gap-1.5">
              {mode === 'SINGLE_TARGET_SWAP' ? (
                <span className={styles.badgeWarn}>{t('withdrawal.auto.modes.SINGLE_TARGET_SWAP.badge')}</span>
              ) : (
                <span className={styles.badgeOk}>{t('withdrawal.auto.modes.DIRECT_BY_NETWORK.badge')}</span>
              )}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
